/* Settings pages (piece 15): one column of cards over the Page Module's Content region, the breadcrumb above. */
.page {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.crumbs {
  margin: 0;
  font-size: var(--font-size-small);
}

.crumbs a {
  color: var(--navy-primary);
}

/* The kind line under a settings page's title (decision 1397): Settings and the
   category, each a link back into the Index, drawn in the banner's own ink. */
.kindLine {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
}

.kindLink {
  color: inherit;
  text-decoration: underline;
}

/*
 * 🔒 The tile's own entries beside the page (decision 2792). From laptop width the
 * rail stands at the content's left and the category half of the kind line stays
 * a link; below it the rail is folded into the category, which opens the same
 * list. A page whose content carries a rail of its own folds it at every width.
 */
.kindLinkWide {
  display: none;
}

.kindMenu {
  display: inline-flex;
}

@media (--bp-lg) {
  .kindLinkWide {
    display: inline;
  }

  .kindMenu:not(.kindMenuAlways) {
    display: none;
  }
}

.kindMenuToggle {
  display: inline-flex;
  align-items: center;
  gap: var(--space-half);
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
}

.kindMenuToggle:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* A page's gear stands at the right end of its title bar, after the banner's one action (decisions 2805, 2815). */
.gearAnchor {
  display: inline-flex;
}

/* The folded list hangs under the category on the page body, as the split button's panel does. */
.kindMenuPanel {
  position: fixed;
  z-index: 20;
  transform: translateY(var(--space-1));
  width: var(--settings-rail-width);
  padding: var(--space-1);
  box-sizing: border-box;
  background: var(--surface);
  border: var(--dropdown-border-width) solid var(--border);
  border-radius: var(--dropdown-radius);
  box-shadow: var(--panel-shadow);
}

.railFrame {
  display: block;
}

.rail {
  display: none;
}

@media (--bp-lg) {
  .railFrame {
    display: grid;
    grid-template-columns: var(--settings-rail-width) minmax(0, 1fr);
    gap: var(--space-4);
    align-items: start;
  }

  /* The rail stays in view while a long page scrolls beside it. */
  .rail {
    position: sticky;
    top: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
}

.railHeading {
  margin: 0;
  padding: 0 var(--space-2);
  color: var(--text-muted);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-semibold);
}

.railList {
  display: flex;
  flex-direction: column;
  gap: var(--space-half);
  margin: 0;
  padding: 0;
  list-style: none;
}

.railEntry {
  display: block;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-small);
  color: var(--text);
  font-size: var(--font-size-small);
  overflow-wrap: anywhere;
}

.railLink {
  text-decoration: none;
}

.railLink:hover {
  background: var(--surface-2);
  text-decoration: underline;
}

.railLink:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* The page the person is on, and the entry a deeper page sits under: the selection's own wash. */
.railCurrent {
  background: var(--selection-bg);
  font-weight: var(--font-weight-semibold);
}

/* A section of a grouped Settings page (decision 0584): the entry that used to
   be a page of its own, under a heading a person can scroll to and link to. The
   heading is the only thing the fold adds — the cards beneath it are the ones
   that page always drew. */
.section {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  scroll-margin-top: var(--space-4);
}

.sectionHeader {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding-bottom: var(--space-1);
  border-bottom: 1px solid var(--tile-border);
}

.sectionTitle {
  margin: 0;
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-bold);
}

.card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-3);
  border: 1px solid var(--tile-border);
  border-radius: var(--radius-medium);
  background: var(--surface);
  box-shadow: var(--tile-shadow);
}

.cardTitle {
  margin: 0;
  font-size: var(--section-label-size);
  font-weight: var(--section-label-weight);
  letter-spacing: var(--section-label-tracking);
  color: var(--section-label-color);
  text-transform: uppercase;
}

.hint {
  margin: 0;
  max-width: var(--measure-help);
  color: var(--text-muted);
  font-size: var(--font-size-small);
}

.row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-3);
}

.row > * {
  flex: 1 1 200px;
}

/* The inputs of an add window stand one above another (decision 2803). */
.addWindowBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.hiddenInput {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: var(--space-2);
}

/* A role's capability ticks (decision 10300): one switch per key, in as many columns as fit, each label wrapping in its own. */
.ticks {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(20rem, 1fr));
  gap: var(--space-2);
  align-items: start;
}

/* Cards a column wide in a grid, as many across as fit, each at its own height (decision 0296). */
.cardGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
  gap: var(--space-4);
  align-items: start;
}

/*
 * A page whose cards are all short (decision 1397; T582 C3): two across from
 * laptop width and one on a phone, so a lone card stands half the pane wide
 * rather than stretched across it. 🔨 Not the auto-fit grid above, which widens a
 * lone card to the whole row. Fields inside a card still go across the card.
 */
.cardPairs {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-4);
  align-items: start;
}

@media (--bp-lg) {
  .cardPairs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/*
 * A list in the middle and its form at the right on a wide screen; stacked below
 * it (decision 0295). 🔒 The one two-column pattern on a settings page (decision
 * 1397; T582 C4): Account assignments and Categories stand on it, and the pattern
 * that never folded, which squeezed the right side to two thirds of a phone, is gone.
 */
.listAndForm {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-4);
  align-items: start;
}

@media (--bp-lg) {
  .listAndForm {
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  }
}

/*
 * 🔨 The two text weights a settings page keeps for itself (T177). The row's own
 * title and detail belong to the detail list now; these are for a card's own
 * heading and a caption beside a field, which are not rows.
 */

.emphasis {
  font-weight: var(--font-weight-semibold);
}

.caption {
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

.textarea {
  width: 100%;
  min-height: var(--field-height);
  padding: var(--space-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-small);
  background: var(--surface);
  color: var(--text);
  font: inherit;
  font-size: var(--font-size-small);
}

.textarea {
  min-height: calc(var(--field-height) * 3);
  resize: vertical;
}

.fieldLabel {
  display: block;
  margin-bottom: var(--space-1);
  font-size: var(--font-size-caption);
  color: var(--text-muted);
}

/* Cc and Bcc address chips (decision 0569): a stadium pill per address, with its own remove control. */
.chipRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin: var(--space-1) 0;
  padding: 0;
  list-style: none;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0 var(--space-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-stadium);
  background: var(--surface-2);
  font-size: var(--font-size-caption);
}

.chipRemove {
  border: 0;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  font: inherit;
  line-height: 1;
  padding: 0;
}

/* The Term Container tree (figure 115): the Preamble row leads, each Section collapses to a count, each clause previews in place. */
.tree {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.treeRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--surface-2);
  border-left: var(--tree-bar-width) solid var(--navy-primary);
  border-radius: var(--radius-small);
  background: var(--surface);
}

.treeRow[data-level='clause'] {
  margin-left: var(--space-6);
  border-left-color: var(--border);
}

.chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--button-height-small);
  height: var(--button-height-small);
  border: 0;
  border-radius: var(--radius-small);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}

.number {
  min-width: var(--space-6);
  color: var(--navy-primary);
  font-weight: var(--font-weight-semibold);
}

.treeTitle {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: var(--font-weight-semibold);
}

.treeRow[data-level='section'] .treeTitle {
  letter-spacing: var(--section-label-tracking);
  color: var(--section-label-color);
  font-size: var(--section-label-size);
  font-weight: var(--section-label-weight);
  text-transform: uppercase;
}

.preview {
  margin: 0 0 var(--space-2) calc(var(--space-6) * 2);
  padding: var(--space-3);
  border-radius: var(--radius-small);
  background: var(--surface-2);
  font-size: var(--font-size-small);
  white-space: pre-line;
}

.count {
  padding: 0 var(--space-2);
  border-radius: var(--radius-stadium);
  background: var(--surface-2);
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

.secret {
  display: inline-block;
  padding: var(--space-1) var(--space-2);
  border: 1px dashed var(--border);
  border-radius: var(--radius-small);
  background: var(--surface-2);
  letter-spacing: 0.1em;
}

/* A link drawn as the small Secondary button, for a New action that goes to another page. */
.linkButton {
  display: inline-flex;
  align-items: center;
  height: var(--button-height-small);
  padding: 0 var(--space-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-small);
  color: var(--navy-primary);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-semibold);
  text-decoration: none;
}

/* The Index's tiles: one per category, each entry a button-like card (decision 0271). */
/* The Index flows its tiles down as many columns as fit, each tile as tall as its cards (decision 0282). */
.indexColumns {
  columns: var(--settings-tile-min-width);
  column-gap: var(--space-3);
}

/* The search box above the tiles (decision 1397): it filters which cards stand, never where the tiles flow. */
.indexPage {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.indexSearch {
  display: flex;
}

.indexTile {
  break-inside: avoid;
  margin-bottom: var(--space-3);
  /* A settings page's category link opens the Index scrolled to this tile (decision 1397). */
  scroll-margin-top: var(--space-4);
}

.indexTile > * {
  max-width: 100%;
}

.entryCards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(var(--settings-card-min-width), 1fr));
  gap: var(--space-2);
}

.entryCard {
  display: flex;
  align-items: center;
  min-height: var(--button-height-medium);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-small);
  background: var(--surface-2);
  color: var(--navy-primary);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-semibold);
  text-decoration: none;
}

.entryCard:hover {
  border-color: var(--navy-primary);
  background: var(--surface);
}

.entryCard:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* A row of small toggles beside a list row: the record types a file type applies to (decision 0299). */
.chipRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  flex-basis: 100%;
}

/* A Settings form lays its fields across the pane rather than down it: as many
   columns as the width allows, one column where it does not (piece 15; product
   owner, 2026-09-14, over the Backup schedule card's three stacked numbers).
   The `min()` is what stacks them — a column never asks for more than the pane
   has, so no media query decides it and nothing overflows on a phone. */
.fieldGrid {
  /* A field column's narrowest useful width, wide enough for a label and its box. */
  --settings-field-min-width: 16rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, var(--settings-field-min-width)), 1fr));
  gap: var(--space-3);
  align-items: start;
}

/* A field whose answer is a list, a paragraph or formatted text takes the whole row. */
.fieldWide {
  grid-column: 1 / -1;
}

/* A value held as a string array, drawn as the tag row: the pills, then the box
   that takes the next one (piece 15, Blocked extensions). */
.chipField {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.chipInput {
  width: 100%;
  height: var(--field-height);
  padding: 0 var(--space-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-small);
  background: var(--surface);
  color: var(--text);
  font: inherit;
  font-size: var(--font-size-small);
}

.chipInput:focus {
  border-color: var(--navy-primary);
}

/* Nothing added yet: the row keeps its height so the box below it does not jump. */
.chipEmpty {
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

/*
 * An open reminder rule's fields (decision 2780, the row list): the shared `.row`
 * squeezed all five to a 200px basis so their labels wrapped mid-phrase over
 * inputs four characters wide (decision 0483). Its own row sizes each field to
 * what it holds and keeps a line of air between wrapped rows.
 */
.ruleFields {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-3);
}

/* A short field takes the width it needs; the two pickers take the room that is left. */
.ruleFields > * {
  flex: 0 0 auto;
}

.ruleFields > .ruleWide {
  flex: 1 1 220px;
  min-width: 0;
}

/*
 * The print template editor (piece 27; decision 0517): one block per region
 * above its geometry fields, with the live preview pane beneath them. The pane
 * is the renderer's own HTML in an iframe, drawn at the page's aspect ratio so
 * a region nudged an inch moves an inch on the screen too.
 */
/* A region's fields (piece 27). An inch value is four characters wide, so its box is a
   fixed column and never a quarter of the page; the label override takes two columns.
   💰 The generic field grid stretched every box to the page width divided by four
   (owner, 2026-09-19: "these field box widths are too wide"). */
.regionFields {
  --settings-region-field-width: 9rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, var(--settings-region-field-width)), var(--settings-region-field-width)));
  gap: var(--space-3);
  align-items: start;
}

.regionWide {
  grid-column: span 2;
}

.regionBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3) 0;
  border-top: 1px solid var(--surface-2);
}

.regionBlock:first-child {
  border-top: 0;
}

.regionTitle {
  margin: 0;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-small);
}

/*
 * 🔒 The pane the sheet is read in (decisions 0577, 0611). It is a frame of its
 * own size that scrolls inside itself: the sheet inside it grows past the pane
 * when a person zooms, and the pane stays where it is, so a page break moves the
 * paper and never the viewing window.
 */
.previewScroll {
  overflow: auto;
  height: var(--print-preview-pane-height);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: var(--space-2);
  background: var(--surface-2);
}

/* 🔒 T191's envelope overlay switch and its profile, one line beneath the zoom (decision 0662). */
.envelopeBar {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
}

/* 🔒 The sheet as a reader sees it, at the step in force: the stage carries the
   drawn size, so the pane scrolls by what is visible and centres it (decision 0611;
   found in review, 2026-09-21). The frame inside is the page's own pixels, scaled
   onto the stage — the sheet it loads is drawn at fixed physical inches, so a frame
   given another box crops or pads it and never scales it. */
.previewStage {
  position: relative;
  flex: none;
  max-width: none;
  border: 1px solid var(--border);
  border-radius: var(--radius-small);
  background: var(--surface);
}

.previewFrame {
  /* The page's own pixels ride on the element and the step arrives as a transform,
     both from the template's page size and the pane's own box (decisions 0577, 0611).
     🔨 The rule and the paper colour are the stage's: a border on the frame would be
     inside the page's own pixels, so the sheet would scroll inside its own frame. */
  display: block;
  max-width: none;
  flex: none;
  border: 0;
  transform-origin: top left;
}

/* 🔒 Decision 0575: the organisation's logo drawn at the box Region 1 gives it, so the
   panel is the size the sheet gets rather than a bigger picture. The two measurements ride on
   the element, in inches from the seeded region, and the picture is scaled into them. */
.logoBox {
  display: block;
  overflow: hidden;
}

.logoBox img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  object-position: left top;
}

/*
 * 🔒 The print template gallery (decision 0615): a rail of document types at the
 * left and the chosen type's cards beside it. The rail is its own content width,
 * so a long type name is not truncated to a fixed column.
 */
.galleryPanes {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-4);
  align-items: start;
}

@media (--bp-md) {
  .galleryPanes {
    grid-template-columns: max-content minmax(0, 1fr);
  }
}

.typeRail,
.sectionRail {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--space-1);
}

@media (--bp-md) {
  .typeRail,
  .sectionRail {
    flex-direction: column;
    flex-wrap: nowrap;
  }
}

.typeRailItem,
.sectionRailItem {
  appearance: none;
  background: none;
  border: 0;
  border-radius: var(--radius-small);
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: var(--text-muted);
  padding: var(--space-2) var(--space-3);
}

/* 🔨 The selected entry is the one the column beside it is showing; it reads as chosen and not as hovered. */
.railItemOn {
  background: var(--surface-2);
  color: var(--accent);
  font-weight: var(--font-weight-semibold);
}

/* Cards as many across as fit, each at its own height, the shape the Settings card grid already uses. */
.templateGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, var(--print-template-card-width)), 1fr));
  gap: var(--space-4);
  align-items: start;
}

.templateCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-medium);
  background: var(--surface);
  cursor: pointer;
}

/*
 * 🔒 The thumbnail: the template's own sample sheet, drawn at the page's ratio and
 * scaled into the card. 🔨 The frame takes no pointer, so a press anywhere on the
 * card opens the template instead of landing inside the frame's own document.
 */
.templateThumb {
  position: relative;
  overflow: hidden;
  align-self: center;
  /* The sheet's own pixels times the step, so the box is exactly the drawn sheet
     and the card never shows a band of paper-coloured nothing beside it. */
  width: calc(var(--print-template-thumb-page-width) * var(--print-template-thumb-scale));
  height: calc(var(--print-template-thumb-page-height) * var(--print-template-thumb-scale));
  border: 1px solid var(--surface-2);
  border-radius: var(--radius-small);
  background: var(--surface);
}

.templateThumbFrame {
  position: absolute;
  top: 0;
  left: 0;
  width: var(--print-template-thumb-page-width);
  height: var(--print-template-thumb-page-height);
  border: 0;
  pointer-events: none;
  transform: scale(var(--print-template-thumb-scale));
  transform-origin: top left;
}

.templateCardFoot {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

/* 🔒 Decision 2791: the name with its tick and Default pill, the first line of every card's foot. */
.templateCardHead {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

/* 🔒 Decision 2791: every card's buttons in one row, which wraps only where the card is narrower than the row. */
.templateCardButtons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.templateCardName {
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-weight: var(--font-weight-semibold);
  color: var(--accent);
  cursor: pointer;
  text-align: left;
}

/* 🔒 T404: a template's name and its Rename, or the box with Save name and Cancel, on one line that wraps. */
.templateNameEditor {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.templateNameText {
  font-weight: var(--font-weight-semibold);
}

/* 🔨 The type's default wears a warm badge, the one thing that distinguishes two cards of the same type. */
.defaultBadge {
  border-radius: var(--radius-stadium);
  padding: var(--space-1) var(--space-2);
  background: var(--status-warning-bg);
  color: var(--status-warning-text);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-semibold);
}

/* 🔒 The last card, dashed, which opens the New customer format flow (decision 0615). */
.newTemplateCard {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-4);
  border: 1px dashed var(--border);
  border-radius: var(--radius-medium);
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  min-height: var(--print-template-card-min-height);
}

.newTemplatePlus {
  color: var(--accent);
  font-weight: var(--font-weight-semibold);
}

/*
 * 🔒 The editor's three panes (decision 0615): the section rail, the properties
 * column at about a third, and the preview at about two thirds. 🔒 The page
 * scrolls once (T556; decision 1280): the properties column flows with the
 * shell's one scrolling region at every width, and the preview pane alone
 * scrolls inside itself (decision 0611). The Save stands in a bar that sticks to
 * the top of that region, so a long column never carries it out of reach.
 */
.editorPanes {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-4);
  align-items: start;
}

@media (--bp-lg) {
  .editorPanes {
    grid-template-columns: max-content minmax(0, 1fr) minmax(0, 2fr);
  }
}

/* 🔒 Positions (decision 1533): the column takes the table's own width and the
   preview the rest, so six columns of inch boxes never scroll inside a third. */
@media (--bp-lg) {
  .editorPanesPositions {
    grid-template-columns: max-content max-content minmax(0, 1fr);
  }
}

/* 🔒 Every region's place in one table (decision 1533): the region named at the
   left, the four inch boxes and the Enabled tick each in a column of its own. */
.positions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
}

/* 🔨 The table sets the width and the rule beneath it wraps to it: at its own
   one-line width the rule widened the column past the preview (found while
   photographing, 2026-10-02). It adds nothing to the column's width and fills it. */
.positionsRule {
  width: 0;
  min-width: 100%;
}

/* 🔨 Below --bp-lg the column is one phone's width and the table is not: the
   table keeps its own width, so no box is squeezed, and scrolls across inside
   this box alone rather than sideways across the whole page (found in review,
   2026-10-02). At the wide breakpoint the column is the table's width and
   nothing scrolls. */
.positionsScroll {
  max-width: 100%;
  overflow-x: auto;
}

/* The data table fills its box by default; the Positions table keeps its own width, as it had before the shared table drew it (T3272). */
.positionsScroll table {
  width: max-content;
}

.positionName {
  font-weight: var(--font-weight-regular);
  white-space: nowrap;
}

/* The row a refusal names wears the error tint, so the one to fix is plain among ten. */
.positionRefused th,
.positionRefused td {
  background: var(--status-error-bg);
}

/* A link in a line of text, drawn as text: Edit on Positions beside a region's place. */
.textLink {
  appearance: none;
  padding: 0;
  border: 0;
  background: none;
  color: var(--navy-primary);
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
}

/* 🔨 The bar sticks inside the scrolling region's own padding, so a strip of that
   padding stands above it where the page scrolled beneath would show through. A
   shadow of the bar's own colour, lifted by that padding, covers the strip and
   moves nothing (T556, found while building 2026-09-29: the page scrolls further
   now the column is uncapped, and the rows showed through above Save). */
.editorBar {
  position: sticky;
  top: 0;
  z-index: var(--print-editor-bar-layer);
  box-shadow: 0 calc(var(--space-3) * -1) 0 0 var(--surface);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) 0;
  background: var(--surface);
}

/* 🔨 No height cap and no overflow of its own (T556; decision 1280): a second
   scrollbar inside the frame's one is the double scroll the owner refused. */
.propertiesColumn {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.previewColumn {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.propertyGroup {
  border-top: 1px solid var(--surface-2);
}

.propertyGroup:first-child {
  border-top: 0;
}

.propertyGroupHead {
  appearance: none;
  background: none;
  border: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: var(--space-2) 0;
  font: inherit;
  cursor: pointer;
  text-align: left;
}

.propertyGroupBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-bottom: var(--space-3);
}

/*
 * 🔒 A region's content rows (piece 27; decision 0656): fixed-width columns —
 * the Show box, the row's own name, the label typed over it, and a width or a
 * size — every box one line tall. Each line is `display: contents`, so its
 * cells take the grid's own columns and every row lines up with its heading.
 */
.contentRows {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding-top: var(--space-2);
}

.contentRowsGrid {
  display: grid;
  grid-template-columns: 2.5rem minmax(0, 1fr);
  column-gap: var(--space-2);
  row-gap: var(--space-1);
  align-items: center;
}

/* 🔨 The name and the label share what the column leaves, so the width box never
   runs off the properties column at a desktop's width (found while building,
   2026-09-23: fixed widths put the width box past the column's edge at 1280). */
.contentRowsGrid[data-print-rows-labelled='true'] {
  grid-template-columns: 2.5rem minmax(0, 1fr) minmax(0, 1fr);
}

.contentRowsGrid[data-print-rows-labelled='true'][data-print-rows-measure='width'] {
  grid-template-columns: 2.5rem minmax(0, 1fr) minmax(0, 1fr) 4rem;
}

.contentRowsGrid[data-print-rows-labelled='false'][data-print-rows-measure='size'] {
  grid-template-columns: 2.5rem minmax(0, 1fr) 4rem;
}

.contentRowsLine {
  display: contents;
}

.contentRowsHead {
  font-size: var(--font-size-caption);
  color: var(--text-muted);
}

.contentRowsName {
  overflow-wrap: anywhere;
}

/*
 * 🔒 One fact to a line, the label beside its box (T194; decision 0657): the
 * inch boxes two to a line, a choice or a sentence one to a line. Each fact is
 * `display: contents`, so its label and its box take the grid's own columns and
 * every box in a group starts at one edge.
 */
.factGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 11rem), 1fr));
  column-gap: var(--space-3);
  row-gap: var(--space-1);
  align-items: center;
}

/* 🔨 Two to a line where the column holds two and one where it does not: each
   short fact is its own row, the label at the left and the box at the right,
   so the boxes of a group stand in one column whichever way it wraps. */
.factGrid .factRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  min-width: 0;
}

.factGridWide {
  display: grid;
  grid-template-columns: fit-content(12rem) minmax(0, 1fr);
  column-gap: var(--space-2);
  row-gap: var(--space-1);
  align-items: center;
}

.factRow {
  display: contents;
}

.factLabel {
  font-size: var(--font-size-caption);
  color: var(--text-muted);
  white-space: nowrap;
}

.factGridWide .factLabel {
  white-space: normal;
}

.columnSetPick {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
}

/* The seven kinds a clause prints on (piece 15; decision 0684): one fieldset, the boxes in a wrapping row. */
.kindPicker {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.kindRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-3);
}

/* 🔒 A form a section's own + opened, beneath that section's bar in the terms library (decision 0771). */
.inPlace {
  margin: var(--space-1) 0 var(--space-2) calc(var(--icon-size-nav) + var(--space-2) * 2);
}

/* A restore rehearsal's report: one line per step, in the page's own type (decision 1196). */
.report {
  margin: 0;
  font-family: inherit;
  white-space: pre-wrap;
}

/* The cards the leave question names (decision 2781): one line each, under the sentence that asks. */
.leaveCards {
  margin: 0;
  padding-left: var(--space-5);
  font-weight: var(--font-weight-semibold);
}

/* 🔒 Row T2841: a Discovery template's Group or List, or its own rows, as one framed block holding the rows that stand in it. */
.discoveryBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-3);
  border: 1px solid var(--tile-border);
  border-radius: var(--radius-medium);
  background: var(--surface-2);
}

/* 🔒 Decision 6110: a Group or List standing in another block draws inside it, indented one spacing step. */
.discoveryNested {
  margin-inline-start: var(--space-3);
}

/* 🔒 Row T2840: a Choice question's options as draggable lines, and a two-way choice drawn as the segmented switch under its label. */
.optionLines {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.switchField {
  display: flex;
  flex-direction: column;
}

/* 🔒 Row T3804 (decision 13291): a domain's overall reading beside the pill that says its DKIM is not set up, and the one sentence on what DKIM adds. */
.domainReading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.domainReadingSentence {
  max-width: var(--measure-help);
}
